/**
 * InlineEditCell - Generic inline edit cell for data tables.
 *
 * Click to edit, blur or Enter to save, Escape to cancel.
 * Per STREAM_UX_PATTERNS.md S4.1 inline edit pattern.
 *
 * Feature-local component - candidate for promotion to shared if used by 3+ features.
 */

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type ReactNode,
} from 'react'

// ── Types ───────────────────────────────────────────────────────────────────

export interface InlineEditCellProps {
  /** Current value */
  value: string
  /** Called when value is saved */
  onSave: (value: string) => Promise<void> | void
  /** Placeholder when empty */
  placeholder?: string
  /** Validation function - returns error message or null */
  validate?: (value: string) => string | null
  /** Whether editing is disabled */
  disabled?: boolean
  /** Input type */
  type?: 'text' | 'email'
  /** Maximum length */
  maxLength?: number
}

// ── Component ───────────────────────────────────────────────────────────────

export function InlineEditCell({
  value,
  onSave,
  placeholder = 'Click to edit',
  validate,
  disabled = false,
  type = 'text',
  maxLength,
}: InlineEditCellProps): ReactNode {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Update edit value when prop changes (while not editing)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value)
    }
  }, [value, isEditing])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = useCallback(
    (e?: React.MouseEvent) => {
      if (disabled || isSaving) return
      // Prevent the click from bubbling to the row (which would trigger row
      // selection in DataTable). Per CustomerList pattern at the ID button.
      e?.stopPropagation()
      setIsEditing(true)
      setError(null)
    },
    [disabled, isSaving]
  )

  const handleCancel = useCallback(() => {
    setIsEditing(false)
    setEditValue(value)
    setError(null)
  }, [value])

  const handleSave = useCallback(async () => {
    // Skip if no change
    if (editValue === value) {
      setIsEditing(false)
      return
    }

    // Validate
    if (validate) {
      const validationError = validate(editValue)
      if (validationError) {
        setError(validationError)
        return
      }
    }

    setIsSaving(true)
    setError(null)

    try {
      await onSave(editValue)
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }, [editValue, value, validate, onSave])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
    },
    [handleSave, handleCancel]
  )

  const handleBlur = useCallback(() => {
    // Small delay to allow click events to register
    setTimeout(() => {
      if (isEditing && !isSaving) {
        handleSave()
      }
    }, 100)
  }, [isEditing, isSaving, handleSave])

  // Editing mode
  if (isEditing) {
    return (
      <div
        style={styles.editContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value)
            setError(null)
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isSaving}
          maxLength={maxLength}
          style={{
            ...styles.input,
            ...(error ? styles.inputError : {}),
          }}
          className="inline-edit-input"
        />
        {error && <div style={styles.error}>{error}</div>}
      </div>
    )
  }

  // Display mode
  return (
    <button
      type="button"
      onClick={(e) => handleStartEdit(e)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      style={{
        ...styles.display,
        ...(isHovered && !disabled ? styles.displayHover : {}),
        ...(disabled ? styles.displayDisabled : {}),
      }}
      className="inline-edit-cell"
      title={disabled ? undefined : 'Click to edit'}
    >
      <span style={styles.displayText}>
        {value || <span style={styles.placeholder}>{placeholder}</span>}
      </span>
    </button>
  )
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  editContainer: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '4px 8px',
    fontSize: '13px',
    color: 'var(--text-primary)',
    background: 'var(--bg-primary)',
    border: '2px solid var(--accent-primary)',
    borderRadius: 'var(--radius-sm)',
    outline: 'none',
    transition: 'border-color var(--transition-fast)',
  },
  inputError: {
    borderColor: 'var(--error)',
  },
  error: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    color: 'var(--error)',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--error)',
    borderRadius: 'var(--radius-sm)',
    whiteSpace: 'nowrap',
    zIndex: 10,
  },
  display: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '4px 8px',
    margin: '-4px -8px',
    fontSize: '13px',
    color: 'var(--text-primary)',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    textAlign: 'left',
    cursor: 'text',
    transition: 'all var(--transition-fast)',
    minWidth: 0,
  },
  displayHover: {
    background: 'var(--bg-tertiary)',
  },
  displayDisabled: {
    cursor: 'default',
    opacity: 0.7,
  },
  displayText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  placeholder: {
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
}

export default InlineEditCell
